import { useState, useEffect, useMemo } from 'react'
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Check, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface AutocompleteItem {
  id: number
  name: string
}

export interface SuggestionGroup<T extends AutocompleteItem> {
  heading: string
  items: T[]
}

export interface AutocompleteProps<T extends AutocompleteItem> {
  items?: T[]
  suggestions?: SuggestionGroup<T>[]
  selectedId: number | null
  selectedName: string
  onSelect: (id: number, name: string) => void
  onClear: () => void
  onCreate?: (name: string) => Promise<void>
  placeholder?: string
  emptyMessage?: string
  createLabel?: string
  itemsHeading?: string
  disabled?: boolean
  isCreating?: boolean
  id?: string
}

export function Autocomplete<T extends AutocompleteItem>({
  items,
  suggestions,
  selectedId,
  selectedName,
  onSelect,
  onClear,
  onCreate,
  placeholder = "Search...",
  emptyMessage = "No items found",
  createLabel = "Create",
  itemsHeading = "All items",
  disabled = false,
  isCreating = false,
  id,
}: AutocompleteProps<T>) {
  const [search, setSearch] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  // Get all items from both items prop and suggestions
  const allItems = useMemo(() => {
    const result: T[] = []
    if (items) result.push(...items)
    if (suggestions) result.push(...suggestions.flatMap(group => group.items))
    return result
  }, [items, suggestions])

  // Filter items based on search (for simple items mode)
  const filteredItems = useMemo(() => {
    if (!items) return []
    if (!search) return items
    return items.filter(item =>
      item.name.toLowerCase().includes(search.toLowerCase())
    )
  }, [items, search])

  // Filter suggestion groups based on search
  const filteredSuggestions = useMemo(() => {
    if (!suggestions) return []
    if (!search) return suggestions

    return suggestions
      .map(group => ({
        ...group,
        items: group.items.filter(item =>
          item.name.toLowerCase().includes(search.toLowerCase())
        )
      }))
      .filter(group => group.items.length > 0)
  }, [suggestions, search])

  // Check if search matches any existing item
  const hasExactMatch = useMemo(() => {
    return allItems.some(item =>
      item.name.toLowerCase() === search.toLowerCase()
    )
  }, [allItems, search])

  const shouldShowCreate = onCreate && search && !hasExactMatch && !isCreating

  const handleSelect = (itemId: number, itemName: string) => {
    onSelect(itemId, itemName)
    setSearch(itemName)
    setTimeout(() => setIsFocused(false), 0)
  }

  const handleCreate = async () => {
    if (!onCreate || !search) return

    try {
      await onCreate(search)
      setTimeout(() => setIsFocused(false), 0)
    } catch (error) {
      console.error('Failed to create item:', error)
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setSearch('')
    onClear()
  }

  // Sync search with selected name when it changes externally
  useEffect(() => {
    if (selectedName && !isFocused) {
      setSearch(selectedName)
    }
  }, [selectedName, isFocused])

  return (
    <Popover open={isFocused && !disabled} onOpenChange={setIsFocused}>
      <PopoverTrigger asChild>
        <Command
          className={cn(
            "w-full rounded-lg border",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onClick={(e) => {
            if (isFocused || disabled) {
              e.preventDefault()
            }
          }}
        >
          <div className="relative flex items-center">
            <CommandInput
              id={id}
              placeholder={placeholder}
              value={search}
              onValueChange={setSearch}
              onFocus={() => !disabled && setIsFocused(true)}
              onBlur={() => {
                setTimeout(() => {
                  if (!document.activeElement?.closest('[role="dialog"]')) {
                    setIsFocused(false)
                  }
                }, 200)
              }}
              className="pr-8"
              disabled={disabled}
            />
            {search && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                onMouseDown={(e) => e.preventDefault()}
                className="absolute right-2 p-1 hover:bg-accent rounded-sm"
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </Command>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement
          if (target.closest('[cmdk-input]') || target.closest('button[type="button"]')) {
            e.preventDefault()
            return
          }
        }}
      >
        <Command>
          <CommandList>
            <CommandEmpty>
              {search ? emptyMessage : "Start typing to search"}
            </CommandEmpty>

            {/* Render suggestion groups */}
            {filteredSuggestions.map((group) => (
              <div key={group.heading}>
                <CommandGroup heading={group.heading}>
                  {group.items.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={item.name}
                      onSelect={() => handleSelect(item.id, item.name)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedId === item.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {item.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator/>
              </div>
            ))}

            {/* Render simple items list */}
            {filteredItems.length > 0 && (
              <CommandGroup heading={suggestions && filteredSuggestions ? itemsHeading : undefined}>
                {filteredItems.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.name}
                    onSelect={() => handleSelect(item.id, item.name)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedId === item.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {item.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Render create option */}
            {shouldShowCreate && (
              <>
                {(filteredItems.length > 0 || filteredSuggestions.length > 0) && <CommandSeparator />}
                <CommandGroup>
                  <CommandItem
                    onSelect={handleCreate}
                    className="text-primary"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {createLabel} &quot;{search}&quot;
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}